import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Unlink, RotateCcw } from "lucide-react";

const err = (e, f) => e?.response?.data?.detail || f;

// Rechnung am Auftrag: Briefpapier neu anwenden, Verknüpfung lösen (Admin), Entwurf in sevDesk löschen und neu erstellen (Admin)
export const RechnungActions = ({ order, user, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // "loesen" | "neu"
  const [entwurf, setEntwurf] = useState(false);
  const admin = user?.rolle === "admin";
  const canLp = ["admin", "manager"].includes(user?.rolle);

  useEffect(() => {
    if (!admin || !order.sevdesk_rechnung_id) return;
    api.get(`/auftraege/${order.id}/rechnung/sevdesk-status`).then((r) => setEntwurf(!!r.data.entwurf)).catch(() => setEntwurf(false));
  }, [admin, order.id, order.sevdesk_rechnung_id]);

  const act = async (key, path, ok) => {
    if (busy) return;
    setBusy(key);
    try { const r = await api.post(`/auftraege/${order.id}/rechnung/${path}`); toast.success(ok(r.data)); onChanged?.(); }
    catch (e) { toast.error(err(e, "Aktion fehlgeschlagen.")); }
    finally { setBusy(""); setConfirm(null); }
  };
  if (!order.rechnungsnummer && !order.sevdesk_rechnung_id) return null;
  const spin = (k, Icon) => (busy === k ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icon className="w-4 h-4 mr-1" />);

  return (
    <>
      {canLp && order.sevdesk_rechnung_id && (
        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act("lp", "briefpapier", (d) => `Briefpapier auf Rechnung angewendet (PDF v${d.rechnung_pdf_version}).`)} data-testid="auftrag-rechnung-briefpapier">
          {spin("lp", RefreshCw)} Briefpapier neu anwenden
        </Button>
      )}
      {admin && entwurf && (
        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setConfirm("neu")} data-testid="auftrag-rechnung-entwurf-neu">{spin("neu", RotateCcw)} Entwurf löschen &amp; neu erstellen</Button>
      )}
      {admin && (
        <Button size="sm" variant="ghost" className="text-rose-700" disabled={!!busy} onClick={() => setConfirm("loesen")} data-testid="auftrag-rechnung-loesen">{spin("loesen", Unlink)} Rechnungsverknüpfung lösen</Button>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <AlertDialogContent data-testid="rechnung-recovery-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "neu" ? "Entwurf in sevDesk löschen und neu erstellen?" : `Rechnung ${order.rechnungsnummer || ""} vom Auftrag lösen?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "neu"
                ? "Der Rechnungsentwurf wird in sevDesk gelöscht und mit den aktuellen Auftragspositionen korrekt neu erstellt (Briefpapier, Skonto, Prüfung der Summen)."
                : "Bitte die fehlerhafte Rechnung in sevDesk löschen bzw. stornieren. Danach kann am Auftrag eine korrekte Rechnung erstellt werden."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy} data-testid="rechnung-recovery-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction disabled={!!busy} data-testid="rechnung-recovery-confirm" onClick={(e) => {
              e.preventDefault();
              if (confirm === "neu") act("neu", "entwurf-neu", (d) => `Rechnung ${d.rechnungsnummer} neu erstellt.`);
              else act("loesen", "verknuepfung-loesen", (d) => `Rechnung ${d.geloest || ""} gelöst. ${d.hinweis}`);
            }}>{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bitte warten…</> : confirm === "neu" ? "Löschen & neu erstellen" : "Verknüpfung lösen"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
